<template>
  <article class="staff-call">
    <div class="section1">
      <BaseTitle2 :title="t('title.staff_call')" />

      <section class="description">
        <i18n-t keypath="general.call_description1" tag="p">
          <template #confirm>
            <span>{{ t('general.confirm') }}</span>
          </template>
        </i18n-t>

        <figure class="figure">
          <img src="@/assets/images/img-staff.jpg" alt="직원 이미지" />
        </figure>
      </section>
    </div>
    <div class="section2">
      <div class="call-messages">
        <ul v-if="menuStore.callMessages.length > 0" class="list-style-none">
          <li v-for="(callMessage, index) in menuStore.callMessages" :key="index">
            <BaseCheckbox
              :id="`message_checkbox_${index}`"
              v-model="checkedMessages"
              :label="getCallMessageByLocale(callMessage)"
              :value="callMessage"
            />
          </li>
        </ul>
      </div>

      <footer>
        <BaseButtonGroup>
          <BaseButton class="outline" :flex="1" @mouseup="handleCancelClick" @touchend.prevent="handleCancelClick">
            <template #icon>
              <svg xmlns="http://www.w3.org/2000/svg" width="29.442" height="25.247" viewBox="0 0 29.442 25.247">
                <path
                  id="arrow-left-solid"
                  d="M.641,75.063a2.106,2.106,0,0,0,0,2.977L11.157,88.556a2.105,2.105,0,0,0,2.977-2.977L7.2,78.652H27.364a2.1,2.1,0,1,0,0-4.206H7.207l6.921-6.927a2.105,2.105,0,1,0-2.977-2.977L.635,75.057Z"
                  transform="translate(-0.025 -63.925)"
                />
              </svg>
            </template>
            {{ t('button.prev') }}
          </BaseButton>
          <BaseButton :flex="2" @mouseup="handleConfirmClick" @touchend.prevent="handleConfirmClick">
            <template #icon>
              <img src="@/assets/images/icon-check-white.svg" alt="확인 아이콘" />
            </template>
            {{ t('button.confirm') }}
          </BaseButton>
        </BaseButtonGroup>
      </footer>
    </div>
  </article>
</template>

<script setup lang="ts">
  import * as Android from '@/api/android';
  import ServiceOrderRequest from '@/api/android/interfaces/ServiceOrderRequest';
  import BaseButton from '@/components/base/BaseButton.vue';
  import BaseButtonGroup from '@/components/base/BaseButtonGroup.vue';
  import BaseCheckbox from '@/components/base/BaseCheckbox.vue';
  import UserError from '@/error';
  import CallMessage from '@/interfaces/CallMessage';
  import { useCommonStore } from '@/stores/common';
  import { useConfigStore } from '@/stores/config';
  import { useMenuStore } from '@/stores/menu';
  import { getCallMessageByLocale } from '@/utils';
  import { ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import BaseTitle2 from './base/BaseTitle2.vue';

  const emits = defineEmits(['close']);

  const { t } = useI18n();
  const configStore = useConfigStore();
  const commonStore = useCommonStore();
  const menuStore = useMenuStore();

  const checkedMessages = ref<CallMessage[]>([]);

  /** 취소 버튼 클릭 */
  const handleCancelClick = () => {
    emits('close');
    if (configStore.config.IsFastOrderUsed) Android.openServiceApp();
  };

  /** 호출하기 버튼 클릭 */
  const handleConfirmClick = async () => {
    if (checkedMessages.value.length === 0) {
      throw new UserError(t('error.select_call'));
    }

    const loadingId = commonStore.showLoading();
    try {
      if (configStore.config.IsFastOrderUsed) {
        // 선택한 호출 메시지로 서비스 주문 데이터 생성
        const requestData = createServiceOrderData();
        // TODO: 패스트오더 연동 호출 메시지 전송
        await Android.postAddServiceOrderAsync(requestData);
      } else {
        // 호출 메시지 전송
        const response = await Android.customerCallAsync({
          CallMessage: checkedMessages.value.map((msg) => msg.Message).join(','),
        });

        // 호출 메시지 전송 성공 여부 검사
        if (response.RET_CODE !== 0) throw new UserError(response.RET_MSG);

        // 호출 성공 후 모달 닫기
        handleCancelClick();
      }
    } finally {
      commonStore.hideLoading(loadingId);
    }
  };

  /** 서비스 주문 데이터 생성 */
  const createServiceOrderData = () => {
    return {
      Pos_Code: configStore.config.PosCode || '',
      Tab_Code: configStore.config.TableCode || '',
      TableName: configStore.config.TableName,
      Items: checkedMessages.value.map((message) => {
        if (!message.ItemCode) throw new UserError('호출 메시지에 연결된 아이템 코드가 없습니다.');
        const item = menuStore.getItem(message.ItemCode);
        if (!item) throw new UserError('호출 메시지에 연결된 아이템이 없습니다.');

        return {
          QTY: 1,
          Price: item.Price,
          Amt: item.Price * 1,
          ITEM_CODE: item.ItemCode,
          Name0: message.Message,
        };
      }),
    } satisfies ServiceOrderRequest;
  };
</script>

<style scoped lang="scss">
  @use '@/assets/scss/variables' as vars;

  .staff-call {
    display: grid;
    grid-template-columns: 1fr 1fr;
    justify-content: center;
    gap: 1.5625vw;
    padding: 2.9851vw;
    height: 100vh;
    @media (max-width: vars.$sm) {
      flex-direction: column;
    }

    .section1 {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 1.4925vw;
      overflow: scroll;
      @media (max-width: vars.$sm) {
        grid-template-columns: 1fr;
        grid-template-rows: 24px auto auto 60px;
        height: 100%;
      }

      .description {
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 0.7463vw;
        word-break: keep-all;
        font-size: 2.9851vw;
        font-weight: 400;
        line-height: 4.4776vw;
        margin-bottom: 0.7463vw;
        @media (max-width: vars.$md) {
          font-size: 22px;
          line-height: 33px;
        }

        span {
          font-weight: 700;
          color: var(--point-color-secondary);
        }

        .figure {
          padding-top: 1.4925vw;
          text-align: center;
          img {
            width: 25.8955vw;
            object-fit: contain;
            @media (max-width: vars.$sm) {
              width: 240px;
            }
          }
        }
      }
    }

    .section2 {
      display: grid;
      grid-template-rows: 1fr auto;
      gap: 1.5625vw;
      height: 100%;
      min-height: 0;

      .call-messages {
        overflow-y: auto;
        li + li {
          margin-top: 2.2388vw;
        }
      }
    }
  }
</style>
